import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { API_ENDPOINTS } from '../config/apiRouting';
import { useAuth } from './AuthContext';

/**
 * ScopeContext — single source of truth for the "what am I managing right now?"
 * question across the Enterprise web app.
 *
 * A user has access to one or more enterprises → concepts → franchisees → stores.
 * Most screens only need `storeId` + `enterpriseId`; we expose the rest so multi-
 * store screens (menu assignment, hierarchical pricing, enterprise analytics)
 * can render pickers at any level.
 *
 * The selected storeId is persisted to localStorage so it survives reloads.
 */

export interface Enterprise {
  enterpriseId: string;
  name?: string;
}

export interface Store {
  storeId: string;
  name?: string;
  storeCode?: string;
  enterpriseId?: string;
  conceptId?: string;
  franchiseeId?: string;
}

export interface Concept {
  conceptId: string;
  name?: string;
  enterpriseId?: string;
}

export interface Franchisee {
  franchiseeId: string;
  name?: string;
  enterpriseId?: string;
}

interface ScopeState {
  ready: boolean;
  loading: boolean;
  error: string | null;
  enterpriseId: string | null;
  storeId: string | null;
  store: Store | null;
  enterprise: Enterprise | null;
  enterprises: Enterprise[];
  stores: Store[];
}

interface ScopeContextValue extends ScopeState {
  setStoreId: (storeId: string | null) => void;
  setEnterpriseId: (enterpriseId: string | null) => void;
  reload: () => Promise<void>;
}

const STORAGE_KEY = 'bso.scope';

type StoredScope = { storeId?: string | null; enterpriseId?: string | null };

const readStored = (): StoredScope => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return {
      storeId: typeof parsed?.storeId === 'string' ? parsed.storeId : null,
      enterpriseId: typeof parsed?.enterpriseId === 'string' ? parsed.enterpriseId : null
    };
  } catch {
    return {};
  }
};

const writeStored = (value: StoredScope) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    /* ignore */
  }
};

const ScopeContext = createContext<ScopeContextValue | undefined>(undefined);

export const useScope = (): ScopeContextValue => {
  const ctx = useContext(ScopeContext);
  if (!ctx) throw new Error('useScope must be used within ScopeProvider');
  return ctx;
};

const extractItems = <T,>(raw: unknown): T[] => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw as T[];
  const anyRaw = raw as any;
  if (Array.isArray(anyRaw.data)) return anyRaw.data as T[];
  if (Array.isArray(anyRaw.stores)) return anyRaw.stores as T[];
  if (Array.isArray(anyRaw.enterprises)) return anyRaw.enterprises as T[];
  if (Array.isArray(anyRaw.items)) return anyRaw.items as T[];
  return [];
};

export const ScopeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, getIdToken } = useAuth();

  const [state, setState] = useState<ScopeState>(() => {
    const stored = readStored();
    return {
      ready: false,
      loading: false,
      error: null,
      enterpriseId: stored.enterpriseId ?? null,
      storeId: stored.storeId ?? null,
      store: null,
      enterprise: null,
      enterprises: [],
      stores: []
    };
  });

  const load = useCallback(async () => {
    if (!isAuthenticated) {
      setState((s) => ({ ...s, ready: false, loading: false, enterprises: [], stores: [] }));
      return;
    }
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const token = await getIdToken();
      const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
      const [entResp, storesResp] = await Promise.allSettled([
        fetch(API_ENDPOINTS.enterprises(), { headers }),
        fetch(API_ENDPOINTS.stores(), { headers })
      ]);

      let enterprises: Enterprise[] = [];
      let stores: Store[] = [];

      if (entResp.status === 'fulfilled' && entResp.value.ok) {
        const body = await entResp.value.json().catch(() => null);
        enterprises = extractItems<Enterprise>(body);
      }
      if (storesResp.status === 'fulfilled' && storesResp.value.ok) {
        const body = await storesResp.value.json().catch(() => null);
        stores = extractItems<Store>(body);
      }

      setState((prev) => {
        const stored = readStored();

        // Pick a default store: stored > first available
        let storeId = stored.storeId && stores.find((s) => s.storeId === stored.storeId)
          ? stored.storeId
          : (stores[0]?.storeId ?? null);

        // Pick enterprise: follow the selected store if possible, else stored, else first
        const storeEnterprise = storeId ? stores.find((s) => s.storeId === storeId)?.enterpriseId : undefined;
        let enterpriseId: string | null =
          storeEnterprise ||
          (stored.enterpriseId && enterprises.find((e) => e.enterpriseId === stored.enterpriseId)
            ? stored.enterpriseId
            : null) ||
          enterprises[0]?.enterpriseId ||
          null;

        // Persist any normalization we did so everyone stays in sync.
        writeStored({ storeId: storeId || null, enterpriseId });

        return {
          ...prev,
          ready: true,
          loading: false,
          error: null,
          enterprises,
          stores,
          storeId: storeId ?? null,
          enterpriseId,
          store: storeId ? stores.find((s) => s.storeId === storeId) || null : null,
          enterprise: enterpriseId ? enterprises.find((e) => e.enterpriseId === enterpriseId) || null : null
        };
      });
    } catch (e) {
      setState((s) => ({
        ...s,
        loading: false,
        ready: true,
        error: e instanceof Error ? e.message : 'Failed to load scope'
      }));
    }
  }, [getIdToken, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      load();
    } else {
      setState((s) => ({ ...s, ready: true, loading: false, enterprises: [], stores: [] }));
    }
  }, [isAuthenticated, load]);

  const setStoreId = useCallback((storeId: string | null) => {
    setState((prev) => {
      const store = storeId ? prev.stores.find((s) => s.storeId === storeId) || null : null;
      const enterpriseId = store?.enterpriseId || prev.enterpriseId;
      writeStored({ storeId, enterpriseId });
      return {
        ...prev,
        storeId,
        store,
        enterpriseId,
        enterprise: enterpriseId ? prev.enterprises.find((e) => e.enterpriseId === enterpriseId) || null : null
      };
    });
  }, []);

  const setEnterpriseId = useCallback((enterpriseId: string | null) => {
    setState((prev) => {
      const enterprise = enterpriseId
        ? prev.enterprises.find((e) => e.enterpriseId === enterpriseId) || null
        : null;
      // If the current store doesn't belong to this enterprise, pick one that does.
      const currentStoreFits = prev.store?.enterpriseId === enterpriseId;
      let storeId = prev.storeId;
      let store = prev.store;
      if (!currentStoreFits) {
        const firstInEnterprise = prev.stores.find((s) => s.enterpriseId === enterpriseId);
        storeId = firstInEnterprise?.storeId ?? null;
        store = firstInEnterprise ?? null;
      }
      writeStored({ storeId, enterpriseId });
      return { ...prev, enterpriseId, enterprise, storeId, store };
    });
  }, []);

  const value = useMemo<ScopeContextValue>(
    () => ({ ...state, setStoreId, setEnterpriseId, reload: load }),
    [state, setStoreId, setEnterpriseId, load]
  );

  return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
};
